import type { Action } from "@/components/MenuButton";

import type { CareerPlanStatusDialogMode } from "./_CareerPlanStatusDialog";
import {
  CAREER_PLAN_STATUS,
  isPublishedCareerPlan,
  isUnpublishedCareerPlan,
  type CareerPlanStatus
} from "./careerPlanDocument";

export const CAREER_PLAN_STATUS_SNACKBAR: Record<CareerPlanStatusDialogMode, string> = {
  publish: "Career Plan published",
  unpublish: "Career Plan unpublished",
  complete: "Career Plan completed",
  delete: "Career Plan deleted"
};

type CareerPlanLifecycleMode = Exclude<CareerPlanStatusDialogMode, "delete">;

const LIFECYCLE_ACTION: Record<CareerPlanLifecycleMode, { text: string; testId: string; outlined?: boolean }> = {
  publish: { text: "Publish", testId: "career-plan-publish" },
  unpublish: { text: "Unpublish", testId: "career-plan-unpublish", outlined: true },
  complete: { text: "Complete", testId: "career-plan-complete", outlined: true }
};

export function careerPlanStatusForDialogMode(mode: CareerPlanLifecycleMode): CareerPlanStatus {
  if (mode === "publish") return CAREER_PLAN_STATUS.PUBLISHED;
  if (mode === "unpublish") return CAREER_PLAN_STATUS.UNPUBLISHED;
  return CAREER_PLAN_STATUS.COMPLETED;
}

export function getCareerPlanLifecycleModes(status: CareerPlanStatus): CareerPlanLifecycleMode[] {
  const modes: CareerPlanLifecycleMode[] = [];
  if (status === CAREER_PLAN_STATUS.DRAFT || isUnpublishedCareerPlan(status)) {
    modes.push("publish");
  }
  if (isPublishedCareerPlan(status)) {
    modes.push("unpublish");
  }
  if (isPublishedCareerPlan(status) || isUnpublishedCareerPlan(status)) {
    modes.push("complete");
  }
  return modes;
}

export function careerPlanDeleteAction(
  onDelete: () => void,
  options?: { menuOnly?: boolean; disabled?: boolean }
): Action {
  return {
    text: "Delete",
    iconName: "delete",
    destructive: true,
    onClick: onDelete,
    testId: "career-plan-delete",
    disabled: options?.disabled,
    ...(options?.menuOnly ? { menuOnly: true } : {})
  };
}

export function getCareerPlanTableActions(
  status: CareerPlanStatus,
  handlers: Record<CareerPlanStatusDialogMode, () => void>
): Action[] {
  return [
    ...getCareerPlanLifecycleModes(status).map((mode) => ({
      text: LIFECYCLE_ACTION[mode].text,
      section: "Manage" as const,
      onClick: handlers[mode],
      testId: LIFECYCLE_ACTION[mode].testId
    })),
    careerPlanDeleteAction(handlers.delete)
  ];
}

export function getCareerPlanDocumentClusterActions({
  status,
  canEdit,
  canDelete,
  canChangeStatus,
  saving,
  statusDialogOpen,
  onSave,
  handlers
}: {
  status: CareerPlanStatus;
  canEdit: boolean;
  canDelete: boolean;
  canChangeStatus: boolean;
  saving: boolean;
  statusDialogOpen: boolean;
  onSave: () => void;
  handlers: Record<CareerPlanStatusDialogMode, () => void>;
}): Action[] | undefined {
  if (!canEdit && !canDelete) return undefined;

  const actions: Action[] = [];
  if (canEdit) {
    const saveInFlight = saving && !statusDialogOpen;
    actions.push({
      text: saveInFlight ? "Saving..." : "Save",
      variant: "outlined",
      disabled: saving,
      onClick: onSave,
      testId: "career-plan-save",
      iconName: saveInFlight ? "progress_activity" : undefined,
      iconClassName: saveInFlight ? "animate-spin w-6 h-6 !block" : undefined
    });
    for (const mode of getCareerPlanLifecycleModes(status)) {
      actions.push({
        text: LIFECYCLE_ACTION[mode].text,
        variant: LIFECYCLE_ACTION[mode].outlined ? "outlined" : undefined,
        disabled: saving || !canChangeStatus,
        onClick: handlers[mode],
        testId: LIFECYCLE_ACTION[mode].testId
      });
    }
  }
  if (canDelete) {
    actions.push(careerPlanDeleteAction(handlers.delete, { menuOnly: true, disabled: saving }));
  }
  return actions;
}
